import { useRouter } from 'next/router';

import { Box, Heading, Paragraph } from '@zillow/constellation-10';

import { useGetListingRecommendations } from '../../../../hooks/queries/properties';

import { REVIEW_PAGE_SECTIONS } from '../../../../constants/ListingDetailConstants';
import { RECS } from '../../../../constants/RecommendationsConstants';

import {
  wizardFlexContainerStyles,
  wizardFormSectionContainerStyles,
  wizardHeadingStyles,
} from '../../../../styles/wizard';

import HideAddressFormField from '../../../PostingPath/HideAddressFormField';
import PageWizard from '../../../Wizard/PageWizard';
import WizardTipCardContainer from '../../../WizardTipCardContainer';

import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const HidePropertyAddress = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  ...rest
}: WizardBodyTemplateProps) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { recommendationsBySection } = useGetListingRecommendations({
    listingAlias,
    isMultifamily: false,
  });

  const hideAddressRec =
    recommendationsBySection[REVIEW_PAGE_SECTIONS.PROPERTY_INFORMATION]?.[RECS.HIDE_STREET_ADDRESS];

  return (
    <PageWizard
      {...rest}
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onGoToNextStep || undefined}
      onBackClick={onGoToPrevStep || undefined}
      onExitTriggered={onExitWizard}
    >
      <Box css={wizardFlexContainerStyles}>
        <Box>
          <Box css={wizardFormSectionContainerStyles.raw()}>
            <Heading level={2} textStyle="heading-sm" css={wizardHeadingStyles}>
              Do you want to hide the property address on this listing?
            </Heading>

            <HideAddressFormField />
          </Box>
        </Box>

        <WizardTipCardContainer header="Showing the property address">
          {hideAddressRec?.outcomeStats && hideAddressRec.source?.tip ? (
            <>
              <Paragraph css={{ marginBottom: '200' }}>
                Displaying the property address can result in receiving up to {hideAddressRec.outcomeStats}% more
                contacts, based on data for similarly priced properties.
              </Paragraph>
              <Paragraph>You can choose to hide it if privacy is a concern.</Paragraph>
              <Paragraph textStyle="body-xs" css={{ color: 'text.subtle', marginBlock: '400' }}>
                {hideAddressRec.source?.tip}
              </Paragraph>
            </>
          ) : (
            <Paragraph>
              You can hide the address if privacy is a concern, but the listing may receive fewer views and contacts
              than listings that show the property address.
            </Paragraph>
          )}
        </WizardTipCardContainer>
      </Box>
    </PageWizard>
  );
};

export default HidePropertyAddress;
